/**
 * Store de categorías de empresas
 * Gestiona las categorías cargadas desde GET /company-categories
 * Usa patrón de caché para evitar múltiples peticiones
 */

import { defineStore } from 'pinia'
import { useCompanyCategoryApi } from '~/composables/api/useCompanyCategoryApi'

export const useCompanyCategoryStore = defineStore('companyCategory', () => {
  // Estado reactivo
  const categories = ref([])
  const loading = ref(false)
  const loaded = ref(false)

  // Promesa compartida para evitar N+1 llamadas simultáneas
  let loadPromise = null

  /**
   * Carga las categorías desde la API
   * Usa patrón de promesa compartida para evitar múltiples llamadas simultáneas
   * Solo carga una vez, las llamadas subsiguientes retornan las categorías cacheadas
   */
  const loadCategories = async () => {
    // Si ya está cargado, retornar inmediatamente
    if (loaded.value) return

    // Si hay una petición en curso, retornar la misma promesa
    if (loadPromise) return loadPromise

    // Crear nueva promesa y guardarla
    loadPromise = (async () => {
      loading.value = true
      try {
        const { getAllCategories } = useCompanyCategoryApi()
        const response = await getAllCategories()
        categories.value = response.data || []
        loaded.value = true
      } catch (error) {
        console.error('[CompanyCategoryStore] Error loading categories:', error)
        throw error
      } finally {
        loading.value = false
        // Limpiar la promesa para permitir reintentos en caso de error
        loadPromise = null
      }
    })()

    return loadPromise
  }

  /**
   * Limpia las categorías (usado en logout)
   */
  const clearCategories = () => {
    categories.value = []
    loaded.value = false
  }

  return {
    // Estado
    categories,
    loading,
    loaded,
    // Actions
    loadCategories,
    clearCategories
  }
})
